import { useCallback, useRef } from 'react';

const INBOUND_CALL_VIBRATION_PATTERN = [300, 200, 300, 200, 300];

export const useInboundCallRingAlert = () => {
    const ringingAudioContextRef = useRef<AudioContext | null>(null);
    const ringingOscillatorsRef = useRef<OscillatorNode[]>([]);
    const ringingGainRef = useRef<GainNode | null>(null);
    const ringingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const vibrationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const stopVibration = useCallback(() => {
        if (vibrationIntervalRef.current) {
            clearInterval(vibrationIntervalRef.current);
            vibrationIntervalRef.current = null;
        }
        if ('vibrate' in navigator) {
            navigator.vibrate(0);
        }
    }, []);

    const startVibration = useCallback(() => {
        if (!('vibrate' in navigator)) return;

        navigator.vibrate(INBOUND_CALL_VIBRATION_PATTERN);
        vibrationIntervalRef.current = setInterval(() => {
            navigator.vibrate(INBOUND_CALL_VIBRATION_PATTERN);
        }, 3000);
    }, []);

    const stopInboundCallAlert = useCallback(() => {
        stopVibration();

        try {
            if (ringingIntervalRef.current) {
                clearInterval(ringingIntervalRef.current);
                ringingIntervalRef.current = null;
            }
            ringingOscillatorsRef.current.forEach((oscillator) => {
                try {
                    oscillator.stop();
                } catch {
                    // oscillator may already be stopped
                }
            });
            ringingOscillatorsRef.current = [];
            if (ringingGainRef.current) {
                ringingGainRef.current.disconnect();
                ringingGainRef.current = null;
            }
            if (ringingAudioContextRef.current) {
                ringingAudioContextRef.current.close().catch(() => undefined);
                ringingAudioContextRef.current = null;
            }
        } catch (error) {
            console.warn('Could not stop inbound call alert:', error);
        }
    }, [stopVibration]);

    const startInboundCallAlert = useCallback(() => {
        try {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            ringingAudioContextRef.current = audioContext;

            const gainNode = audioContext.createGain();
            gainNode.connect(audioContext.destination);
            ringingGainRef.current = gainNode;

            const osc1 = audioContext.createOscillator();
            const osc2 = audioContext.createOscillator();
            osc1.type = 'sine';
            osc2.type = 'sine';
            osc1.frequency.setValueAtTime(425, audioContext.currentTime);
            osc2.frequency.setValueAtTime(480, audioContext.currentTime);
            osc1.connect(gainNode);
            osc2.connect(gainNode);
            osc1.start();
            osc2.start();
            ringingOscillatorsRef.current = [osc1, osc2];

            const ringDuration = 1;
            const pauseDuration = 2.2;
            const cycleDuration = ringDuration + pauseDuration;

            const scheduleRingCycle = (startTime: number) => {
                gainNode.gain.cancelScheduledValues(startTime);
                gainNode.gain.setValueAtTime(0, startTime);
                gainNode.gain.linearRampToValueAtTime(0.18, startTime + 0.03);
                gainNode.gain.setValueAtTime(0.18, startTime + ringDuration - 0.05);
                gainNode.gain.linearRampToValueAtTime(0, startTime + ringDuration);
                gainNode.gain.setValueAtTime(0, startTime + cycleDuration);
            };

            scheduleRingCycle(audioContext.currentTime);
            ringingIntervalRef.current = setInterval(() => {
                if (ringingAudioContextRef.current && ringingGainRef.current) {
                    scheduleRingCycle(ringingAudioContextRef.current.currentTime);
                }
            }, cycleDuration * 1000);

            startVibration();
        } catch (error) {
            console.warn('Could not start inbound call alert:', error);
        }
    }, [startVibration]);

    return {
        startInboundCallAlert,
        stopInboundCallAlert,
    };
};
